// src/hooks/useProductionBatchStats.js
//
// Fetches production batches using the direct case_id FK on production_batches.
//
// Two queries:
//   allBatches  – all batches for this dentist via cases.user_profile_id
//   caseBatches – batches where production_batches.case_id = caseId (direct)

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  pending:         'En attente',
  in_progress:     'En cours',
  printing:        'Impression',
  post_processing: 'Post-traitement',
  quality_check:   'Contrôle qualité',
  packaging:       'Emballage',
  completed:       'Terminé',
  cancelled:       'Annulé',
  delayed:         'Retardé',
  on_hold:         'En pause',
};

const STATUS_COLORS = {
  pending:         'bg-warning/15 text-warning border-warning/20',
  in_progress:     'bg-primary/15 text-primary border-primary/20',
  printing:        'bg-blue-500/15 text-blue-600 border-blue-500/20',
  post_processing: 'bg-purple-500/15 text-purple-600 border-purple-500/20',
  quality_check:   'bg-orange-500/15 text-orange-600 border-orange-500/20',
  packaging:       'bg-success/15 text-success border-success/20',
  completed:       'bg-success/15 text-success border-success/20',
  cancelled:       'bg-muted text-muted-foreground border-border',
  delayed:         'bg-error/15 text-error border-error/20',
  on_hold:         'bg-muted text-muted-foreground border-border',
};

function normaliseBatch(b) {
  return {
    id:             b.id,
    number:         b.batch_number,
    patientName:    b.patient_name,
    clinicName:     b.clinic_name,
    status:         b.status,
    statusLabel:    STATUS_LABELS[b.status] ?? b.status,
    statusColor:    STATUS_COLORS[b.status] ?? STATUS_COLORS.pending,
    priority:       b.priority,
    progress:       b.progress ?? 0,
    startDate:      b.start_date      ? new Date(b.start_date).toLocaleDateString('fr-FR')      : '—',
    deadline:       b.deadline        ? new Date(b.deadline).toLocaleDateString('fr-FR')        : '—',
    completionDate: b.completion_date ? new Date(b.completion_date).toLocaleDateString('fr-FR') : null,
    notes:          b.notes,
    caseId:         b.case_id ?? null,
  };
}

function summariseByStatus(batches) {
  return batches.reduce((acc, b) => {
    acc[b.status] = (acc[b.status] ?? 0) + 1;
    return acc;
  }, {});
}

// ─────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────
export function useProductionBatchStats(clientUserId, caseId = null) {
  const [state, setState] = useState({
    allBatches:  [],
    caseBatches: [],
    allSummary:  {},
    caseSummary: {},
    loading: true,
    error:   null,
  });

  const fetchData = useCallback(async () => {
    if (!clientUserId) {
      setState(s => ({ ...s, loading: false, error: 'clientUserId manquant' }));
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      // ── 1. Get all case IDs for this dentist ──────────────────────────────
      const { data: userProfile, error: profileErr } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', clientUserId)
        .single();

      if (profileErr) throw profileErr;

      const profileId = userProfile.id;

      const { data: cases, error: casesErr } = await supabase
        .from('cases')
        .select('id')
        .eq('user_profile_id', profileId);

      if (casesErr) throw casesErr;

      const caseIds = (cases ?? []).map(c => c.id);

      // ── 2. All batches linked to this dentist's cases ─────────────────────
      let allBatches = [];
      if (caseIds.length > 0) {
        const { data: allData, error: allErr } = await supabase
          .from('production_batches')
          .select(`
            id, batch_number, patient_name, clinic_name,
            status, priority, progress,
            start_date, deadline, completion_date, notes, case_id
          `)
          .in('case_id', caseIds)
          .order('deadline', { ascending: true });

        if (allErr) throw allErr;
        allBatches = (allData ?? []).map(normaliseBatch);
      }

      // ── 3. Case-scoped batches via direct case_id FK ──────────────────────
      let caseBatches = [];
      if (caseId) {
        const { data: caseData, error: caseErr } = await supabase
          .from('production_batches')
          .select(`
            id, batch_number, patient_name, clinic_name,
            status, priority, progress,
            start_date, deadline, completion_date, notes, case_id
          `)
          .eq('case_id', caseId)
          .order('deadline', { ascending: true });

        if (caseErr) throw caseErr;
        caseBatches = (caseData ?? []).map(normaliseBatch);
      }

      setState({
        allBatches,
        caseBatches,
        allSummary:  summariseByStatus(allBatches),
        caseSummary: summariseByStatus(caseBatches),
        loading: false,
        error:   null,
      });
    } catch (err) {
      console.error('[useProductionBatchStats]', err);
      setState(s => ({
        ...s,
        loading: false,
        error: err.message ?? 'Erreur inconnue',
      }));
    }
  }, [clientUserId, caseId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { ...state, refetch: fetchData };
}